import * as React from "react";

// MUI
import {
	withStyles,
	WithStyles,
	createStyles,
	Theme,
} from "@material-ui/core/styles";
import {
	Typography,
	Grid,
	Avatar,
	Accordion,
	AccordionSummary,
	AccordionDetails,
} from "@material-ui/core";

// Utility
import Utility from "../../domain/classes/Utility";

// Components
import { AnimatedAppointmentTypeItem } from "./AppointmentTypeItem.Component";

// Interfaces
import IAppointmentType from "../../domain/interfaces/IAppointmentType";

// NPM
import { animated, useTrail, config, interpolate } from "react-spring";
import IAnimatable from "../../domain/interfaces/IAnimatable";

export const styles = (theme: Theme) =>
	createStyles({
		root: {},
		accordionHeaderContainer: {
			display: "flex",
			alignItems: "center",
		},
		imgContainer: {
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			marginRight: theme.spacing(2),
			[theme.breakpoints.down("xs")]: {
				marginRight: theme.spacing(1),
			},
		},
		img: {},
	});

export interface AppointmentTypeGroupProps extends IAnimatable {
	style?: React.CSSProperties;
	apptTypes: IAppointmentType[];
	category: string;
	expanded: string | false;
	onAccordionChange: (
		panel: string | false
	) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => void;
}

const AppointmentTypeGroup = React.forwardRef<
	HTMLDivElement,
	AppointmentTypeGroupProps & WithStyles<typeof styles>
>(
	(
		{
			classes,
			style,
			category,
			apptTypes,
			onAccordionChange,
			expanded,
			hasAnimation,
		},
		ref
	) => {
		//Springs
		const trail = useTrail(apptTypes.length, {
			config: config.wobbly,
			opacity: category === expanded ? 1 : 0,
			y: category === expanded ? 0 : -20,
			from: {
				opacity: 0,
				y: -20,
			},
		});

		return (
			<Accordion
				ref={ref}
				className={classes.root}
				style={style}
				square
				onChange={onAccordionChange(category)}
				expanded={expanded === category}
			>
				<AccordionSummary>
					<div className={classes.accordionHeaderContainer}>
						<div className={classes.imgContainer}>
							<Avatar
								src={
									apptTypes.length > 0
										? apptTypes[0].photoUri
										: ""
								}
								className={classes.img}
							/>
						</div>
						<Typography variant="h6" color="primary">
							<strong>{category}</strong>
						</Typography>
					</div>
				</AccordionSummary>
				<AccordionDetails>
					<Grid container spacing={2}>
						{hasAnimation
							? trail.map(({ y, opacity, ...rest }, index) => {
									const currentApptType: IAppointmentType =
										apptTypes[index];

									return (
										<AnimatedAppointmentTypeItem
											clickable
											style={{
												opacity,
												transform: interpolate(
													[y],
													(y) => `translateY(${y}px)`
												),
												...rest,
											}}
											apptType={currentApptType}
											key={
												currentApptType.appointmentTypeName
											}
										/>
									);
							  })
							: apptTypes.map((apptType: IAppointmentType) => {
									return (
										<AnimatedAppointmentTypeItem
											clickable
											apptType={apptType}
											key={apptType.appointmentTypeName}
										/>
									);
							  })}
					</Grid>
				</AccordionDetails>
			</Accordion>
		);
	}
);

const StyledAppointmentTypeGroup = withStyles(styles, {
	classNamePrefix: Utility.generateClassName("AppointmentTypeGroup"),
})(AppointmentTypeGroup);

export default StyledAppointmentTypeGroup;

export const AnimatedAppointmentTypeGroup = animated(
	StyledAppointmentTypeGroup
);
